import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { 
  Lock, Bell, Plus, Trash2, Pause, Play, Crown, 
  TrendingDown, Clock, CheckCircle, AlertTriangle,
  Smartphone, Mail, BellRing, X, Loader, Target,
  Zap, ChevronDown, ChevronUp
} from 'lucide-react';
import { toast } from 'sonner';



const RATE_TYPES = [
  { id: 'fixed_3y', label: '3-Year Fixed', description: 'Best 3-year fixed mortgage rate' },
  { id: 'fixed_5y', label: '5-Year Fixed', description: 'Best 5-year fixed mortgage rate' },
  { id: 'variable_3y', label: '3-Year Variable', description: 'Best 3-year variable rate' },
  { id: 'variable_5y', label: '5-Year Variable', description: 'Best 5-year variable rate' },
  { id: 'prime_rate', label: 'Prime Rate', description: 'Bank prime lending rate' },
  { id: 'boc_rate', label: 'BoC Policy Rate', description: 'Bank of Canada overnight rate' }
];

const RateLockAlerts = ({ user, currentRates }) => {
  const [rateLocks, setRateLocks] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [creating, setCreating] = useState(false);
  
  const [newLock, setNewLock] = useState({
    rate_type: 'fixed_5y',
    target_rate: '',
    notify_sms: true,
    notify_email: true,
    notify_push: true,
    notes: ''
  });
  
  const isPro = user?.subscription_tier === 'pro';
  
  useEffect(() => {
    if (isPro) {
      loadRateLocks();
      loadNotifications();
    } else {
      setLoading(false);
    }
  }, [isPro]);
  
  const loadRateLocks = async () => {
    try {
      const response = await api.get(`/api/rate-locks`);
      setRateLocks(response.data.rate_locks || []);
    } catch (error) {
      console.error('Error loading rate locks:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const loadNotifications = async () => {
    try {
      const response = await api.get(`/api/push-notifications`);
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error('Error loading notifications:', error);
    }
  };
  
  const createRateLock = async () => {
    if (!newLock.target_rate) {
      toast.error('Please enter a target rate');
      return;
    }
    
    setCreating(true);
    try {
      const response = await api.post(
        `/api/rate-locks`,
        {
          ...newLock,
          target_rate: parseFloat(newLock.target_rate)
        }
      );
      
      toast.success('Rate lock created! You\'ll be notified when this rate is available.');
      setRateLocks([response.data.lock, ...rateLocks]);
      setShowCreateModal(false);
      setNewLock({
        rate_type: 'fixed_5y',
        target_rate: '',
        notify_sms: true,
        notify_email: true,
        notify_push: true,
        notes: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create rate lock');
    } finally {
      setCreating(false);
    }
  };
  
  const deleteRateLock = async (lockId) => {
    try {
      await api.delete(`/api/rate-locks/${lockId}`);
      setRateLocks(rateLocks.filter(l => l.lock_id !== lockId));
      toast.success('Rate lock deleted');
    } catch (error) {
      toast.error('Failed to delete rate lock');
    }
  };
  
  const togglePause = async (lock) => {
    try {
      const endpoint = lock.status === 'paused' ? 'resume' : 'pause';
      await api.post(`/api/rate-locks/${lock.lock_id}/${endpoint}`, {});
      
      setRateLocks(rateLocks.map(l => 
        l.lock_id === lock.lock_id 
          ? { ...l, status: lock.status === 'paused' ? 'active' : 'paused' }
          : l
      ));
      
      toast.success(lock.status === 'paused' ? 'Rate lock resumed' : 'Rate lock paused');
    } catch (error) {
      toast.error('Failed to update rate lock');
    }
  };
  
  const markAllRead = async () => {
    try {
      await api.post(`/api/push-notifications/mark-all-read`, {});
      setNotifications(notifications.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking notifications read:', error);
    }
  };
  
  const getCurrentRate = (rateType) => {
    if (!currentRates) return null;
    if (rateType === 'boc_rate') return currentRates.boc_rate;
    if (rateType === 'prime_rate') return currentRates.prime_rate;
    
    const rates = currentRates.rates || [];
    let best = null;
    for (const rate of rates) {
      if (rate[rateType] && (!best || rate[rateType] < best)) {
        best = rate[rateType];
      }
    }
    return best;
  };
  
  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-500/20 text-green-400';
      case 'paused': return 'bg-yellow-500/20 text-yellow-400';
      case 'triggered': return 'bg-purple-500/20 text-purple-400';
      default: return 'bg-muted text-muted-foreground';
    }
  };
  
  if (!isPro) {
    return (
      <div className="bg-gradient-to-br from-purple-500/10 to-indigo-500/10 border border-purple-500/20 rounded-xl p-6">
        <div className="flex items-start gap-4">
          <div className="h-14 w-14 bg-purple-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
            <Target className="h-7 w-7 text-purple-400" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-heading font-semibold flex items-center gap-2 mb-2">
              Rate Lock Alerts
              <span className="px-2 py-0.5 bg-purple-500/30 text-purple-300 text-xs rounded-full flex items-center gap-1">
                <Crown className="h-3 w-3" />
                Pro
              </span>
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              Set a target rate and get instant SMS + email notifications the moment rates drop to your target. 
              Never miss the perfect rate window again.
            </p>
            <div className="flex flex-wrap gap-3 mb-4">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Smartphone className="h-4 w-4 text-purple-400" />
                Instant SMS
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Mail className="h-4 w-4 text-purple-400" />
                Email Alerts
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <BellRing className="h-4 w-4 text-purple-400" />
                Push Notifications
              </div>
            </div>
            <a
              href="/dashboard?tab=settings"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-purple-500 text-white rounded-lg text-sm font-medium hover:bg-purple-600 transition-colors"
            >
              <Lock className="h-4 w-4" />
              Upgrade to Pro
            </a>
          </div>
        </div>
      </div>
    );
  }
  
  if (loading) {
    return (
      <div className="bg-card border border-border rounded-xl p-6 flex items-center justify-center">
        <Loader className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }
  
  const activeLocks = rateLocks.filter(l => l.status === 'active');
  const triggeredLocks = rateLocks.filter(l => l.status === 'triggered');
  
  return (
    <div data-testid="rate-lock-alerts" className="space-y-4">
      {/* Header */}
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 bg-purple-500/20 rounded-xl flex items-center justify-center">
              <Target className="h-6 w-6 text-purple-400" />
            </div>
            <div>
              <h3 className="text-lg font-heading font-semibold flex items-center gap-2">
                Rate Lock Alerts
                <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 text-xs rounded-full">Pro</span>
              </h3>
              <p className="text-sm text-muted-foreground">
                Get instant notifications when rates hit your target
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Notifications Bell */}
            <div className="relative">
              <button
                onClick={() => setShowNotifications(!showNotifications)}
                className="p-2 bg-muted hover:bg-muted/80 rounded-lg transition-colors relative"
              >
                <Bell className="h-5 w-5" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 h-5 w-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                    {unreadCount}
                  </span>
                )}
              </button>
              
              {/* Notifications Dropdown */}
              {showNotifications && (
                <div className="absolute right-0 top-12 w-80 bg-card border border-border rounded-xl shadow-xl z-50">
                  <div className="flex items-center justify-between p-3 border-b border-border">
                    <span className="font-semibold text-sm">Notifications</span>
                    {unreadCount > 0 && (
                      <button onClick={markAllRead} className="text-xs text-primary hover:underline">
                        Mark all read
                      </button>
                    )}
                  </div>
                  <div className="max-h-64 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-4">No notifications</p>
                    ) : (
                      notifications.slice(0, 5).map(notif => (
                        <div 
                          key={notif.notification_id}
                          className={`p-3 border-b border-border/50 ${!notif.read ? 'bg-primary/5' : ''}`}
                        >
                          <div className="flex items-start gap-2">
                            <Zap className="h-4 w-4 text-purple-400 mt-0.5" />
                            <div className="flex-1">
                              <p className="text-sm font-medium">{notif.title}</p>
                              <p className="text-xs text-muted-foreground">{notif.message}</p>
                              <p className="text-xs text-muted-foreground mt-1">
                                {new Date(notif.created_at).toLocaleString()}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
            
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-purple-500 text-white rounded-lg text-sm font-medium hover:bg-purple-600 transition-colors"
            >
              <Plus className="h-4 w-4" />
              New Rate Lock
            </button>
          </div>
        </div>
        
        {/* Active Locks */}
        {activeLocks.length === 0 && triggeredLocks.length === 0 ? (
          <div className="text-center py-8 bg-muted/20 rounded-lg">
            <Target className="h-10 w-10 mx-auto mb-3 text-muted-foreground opacity-50" />
            <p className="text-muted-foreground mb-2">No rate locks set</p>
            <p className="text-sm text-muted-foreground">Create a rate lock to get notified instantly when rates drop</p>
          </div>
        ) : (
          <div className="space-y-3">
            {activeLocks.map(lock => {
              const currentRate = getCurrentRate(lock.rate_type);
              const diff = currentRate ? (currentRate - lock.target_rate).toFixed(2) : null;
              
              return (
                <div key={lock.lock_id} className="bg-muted/20 rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`px-2 py-0.5 rounded-full text-xs ${getStatusColor(lock.status)}`}>
                          {lock.status === 'active' ? 'Watching' : lock.status}
                        </span>
                        <span className="text-xs text-muted-foreground font-mono">{lock.lock_id}</span>
                      </div>
                      
                      <h4 className="font-semibold mb-1">
                        {RATE_TYPES.find(r => r.id === lock.rate_type)?.label || lock.rate_type}
                      </h4>
                      
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div>
                          <p className="text-muted-foreground text-xs">Target Rate</p>
                          <p className="font-bold text-primary text-lg">{lock.target_rate}%</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground text-xs">Current Rate</p>
                          <p className="font-bold text-lg">{currentRate ? `${currentRate}%` : 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground text-xs">Difference</p>
                          <p className={`font-bold text-lg ${diff && parseFloat(diff) <= 0 ? 'text-green-400' : 'text-yellow-400'}`}>
                            {diff ? `${diff > 0 ? '+' : ''}${diff}%` : 'N/A'}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-3 mt-3">
                        {lock.notify_sms && (
                          <span className="flex items-center gap-1 text-xs text-muted-foreground">
                            <Smartphone className="h-3 w-3" /> SMS
                          </span>
                        )}
                        {lock.notify_email && (
                          <span className="flex items-center gap-1 text-xs text-muted-foreground">
                            <Mail className="h-3 w-3" /> Email
                          </span>
                        )}
                        {lock.notify_push && (
                          <span className="flex items-center gap-1 text-xs text-muted-foreground">
                            <Bell className="h-3 w-3" /> Push
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => togglePause(lock)}
                        className="p-2 bg-muted hover:bg-muted/80 rounded-lg transition-colors"
                        title={lock.status === 'paused' ? 'Resume' : 'Pause'}
                      >
                        {lock.status === 'paused' ? (
                          <Play className="h-4 w-4" />
                        ) : (
                          <Pause className="h-4 w-4" />
                        )}
                      </button>
                      <button
                        onClick={() => deleteRateLock(lock.lock_id)}
                        className="p-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-lg transition-colors"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
            
            {/* Triggered Locks */}
            {triggeredLocks.length > 0 && (
              <div className="mt-4 pt-4 border-t border-border">
                <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-purple-400" />
                  Recently Triggered
                </h4>
                {triggeredLocks.map(lock => (
                  <div key={lock.lock_id} className="bg-purple-500/10 border border-purple-500/20 rounded-lg p-3 mb-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="font-medium">
                          {RATE_TYPES.find(r => r.id === lock.rate_type)?.label}
                        </span>
                        <span className="text-sm text-muted-foreground ml-2">
                          hit {lock.triggered_rate}% (target: {lock.target_rate}%)
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">
                          {new Date(lock.triggered_at).toLocaleDateString()}
                        </span>
                        <button
                          onClick={() => deleteRateLock(lock.lock_id)}
                          className="p-1 hover:bg-muted rounded"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Target className="h-5 w-5 text-purple-400" />
                Create Rate Lock
              </h3>
              <button onClick={() => setShowCreateModal(false)} className="p-1 hover:bg-muted rounded">
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Rate Type</label>
                <select
                  value={newLock.rate_type}
                  onChange={(e) => setNewLock({ ...newLock, rate_type: e.target.value })}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg"
                >
                  {RATE_TYPES.map(type => (
                    <option key={type.id} value={type.id}>{type.label}</option>
                  ))}
                </select>
                <p className="text-xs text-muted-foreground mt-1">
                  {RATE_TYPES.find(r => r.id === newLock.rate_type)?.description}
                </p>
                {getCurrentRate(newLock.rate_type) && (
                  <p className="text-xs text-primary mt-1">
                    Current best: {getCurrentRate(newLock.rate_type)}%
                  </p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Target Rate (%)</label>
                <input
                  type="number"
                  step="0.01"
                  value={newLock.target_rate}
                  onChange={(e) => setNewLock({ ...newLock, target_rate: e.target.value })}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg"
                  placeholder="e.g., 4.25"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  You'll be notified when the rate drops to or below this target
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Notification Methods</label>
                <div className="space-y-2">
                  <label className="flex items-center gap-3 p-2 bg-muted/20 rounded-lg cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newLock.notify_sms}
                      onChange={(e) => setNewLock({ ...newLock, notify_sms: e.target.checked })}
                      className="rounded"
                    />
                    <Smartphone className="h-4 w-4 text-purple-400" />
                    <span className="text-sm">SMS (Instant)</span>
                  </label>
                  <label className="flex items-center gap-3 p-2 bg-muted/20 rounded-lg cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newLock.notify_email}
                      onChange={(e) => setNewLock({ ...newLock, notify_email: e.target.checked })}
                      className="rounded"
                    />
                    <Mail className="h-4 w-4 text-purple-400" />
                    <span className="text-sm">Email</span>
                  </label>
                  <label className="flex items-center gap-3 p-2 bg-muted/20 rounded-lg cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newLock.notify_push}
                      onChange={(e) => setNewLock({ ...newLock, notify_push: e.target.checked })}
                      className="rounded"
                    />
                    <Bell className="h-4 w-4 text-purple-400" />
                    <span className="text-sm">In-App Notification</span>
                  </label>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Notes (Optional)</label>
                <input
                  type="text"
                  value={newLock.notes}
                  onChange={(e) => setNewLock({ ...newLock, notes: e.target.value })}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg"
                  placeholder="e.g., For primary residence refinance"
                />
              </div>
              
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 px-4 py-2 bg-muted hover:bg-muted/80 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={createRateLock}
                  disabled={creating || !newLock.target_rate}
                  className="flex-1 px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors disabled:opacity-50"
                >
                  {creating ? <Loader className="h-4 w-4 animate-spin mx-auto" /> : 'Create Rate Lock'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RateLockAlerts;
